import { create } from 'zustand';

export interface GeoChoice {
  lat: number;
  lng: number;
  display: string;
}

interface SearchState {
  query: string;         // city/town location search
  contentQuery: string;  // content search (artists, events, articles, etc.)
  country: string;
  geoChoices: GeoChoice[]; // disambiguation options for city search
  selectedGeoChoice: GeoChoice | null; // user-selected location from dropdown

  // Actions
  setQuery: (q: string) => void;
  setContentQuery: (q: string) => void;
  setCountry: (c: string) => void;
  setGeoChoices: (choices: GeoChoice[]) => void;
  selectGeoChoice: (choice: GeoChoice) => void;
  clearAll: () => void;
}

const STORAGE_KEY = 'artrouting-search';

function loadFromStorage(): { query: string; contentQuery: string; country: string } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        query: typeof parsed.query === 'string' ? parsed.query : '',
        contentQuery: typeof parsed.contentQuery === 'string' ? parsed.contentQuery : '',
        country: typeof parsed.country === 'string' ? parsed.country : '',
      };
    }
  } catch { /* ignore */ }
  return { query: '', contentQuery: '', country: '' };
}

function saveToStorage(state: { query: string; contentQuery: string; country: string }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

const initial = loadFromStorage();

export const useSearchStore = create<SearchState>((set, get) => ({
  query: initial.query,
  contentQuery: initial.contentQuery,
  country: initial.country,
  geoChoices: [],
  selectedGeoChoice: null,

  setQuery: (q: string) => {
    const s = get();
    const next = { query: q, contentQuery: s.contentQuery, country: s.country };
    saveToStorage(next);
    set({ query: q });
  },

  setContentQuery: (q: string) => {
    const s = get();
    const next = { query: s.query, contentQuery: q, country: s.country };
    saveToStorage(next);
    set({ contentQuery: q });
  },

  setCountry: (c: string) => {
    const s = get();
    const next = { query: s.query, contentQuery: s.contentQuery, country: c };
    saveToStorage(next);
    set({ country: c });
  },

  setGeoChoices: (choices: GeoChoice[]) => set({ geoChoices: choices }),
  selectGeoChoice: (choice: GeoChoice) => set({ selectedGeoChoice: choice, geoChoices: [] }),

  clearAll: () => {
    saveToStorage({ query: '', contentQuery: '', country: '' });
    set({ query: '', contentQuery: '', country: '', geoChoices: [], selectedGeoChoice: null });
  },
}));
